import { useState, useEffect, useRef } from "react";
import "./UpdateProfile.css";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import { validateEmail } from "../../../utils";
import toast from "react-hot-toast";
import Grid from "@mui/material/Grid";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import {
  updateProfileImage,
  updateUserByUsername,
  uploadInsurance,
  uploadRegistration,
} from "../../../services";
import { useUser } from "../../../contexts/UserContext";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const powers = [
  "No Power Required",
  "16 Amp Single Phase",
  "30 Amp Service",
  "32 Amp Single Phase",
  "50 Amp Service",
  "63 Amp 3-Phase",
  "63 Amp Single Phase",
  "100 Amp Single Phase",
  "125 Amp 3-Phase",
  "480V 3-Phase 100 amp",
  "480V 3-Phase 200 amp",
  "Hard wire 480V 3-Phase 600amp",
  "Other , please specify"
];

export const UpdateProfile = ({ type, data, token }) => {
  const baseUrl = process.env.REACT_APP_API_URL;
  const defaultProfileImage =
    "https://t3.ftcdn.net/jpg/03/46/83/96/360_F_346839683_6nAPzbhpSkIpb8pmAwufkC7c5eD7wYws.jpg";

  const imageRef = useRef(null);
  const uploadVesselInsuranceRef = useRef(null);
  const uploadVesselRegistrationRef = useRef(null);

  const {refreshUserData} = useUser()

  const [values, setValues] = useState({});
  const [errorField, setErrorField] = useState("");
  const [inputFieldError, setInputFieldError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [insurance, setInsurance] = useState("no file chosen");
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [insuranceUrl, setInsuranceUrl] = useState(null);
  const [registration, setRegistration] = useState("no file chosen");
  const [registrationFile, setRegistrationFile] = useState(null);
  const [registrationUrl, setRegistrationUrl] = useState(null);
  const [userId, setUserId] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  // Handles the "Other" option for power required field
  const [userSpecifiedPowerRequired, setUserSpecifiedPowerRequired] = useState("");
  const [showOtherPowerRequiredTextField, setShowOtherPowerRequiredTextField] = useState(false);

  const imageTypes = ["image/avif", "image/webp"];
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file === undefined) {
      return;
    }

    if (imageTypes.includes(file.type)) {
      toast.error("File type not allowed");
      return;
    }
    const url = URL.createObjectURL(file);
    setImageUrl(url);
    setImageFile(file);
    return;
  };

  const handleImageUpload = async () => {
    setImageLoading(true);
    const res = await updateProfileImage(imageFile, token);

    if (res?.status === true) {
      toast.success("Profile image uploaded successfully");
      setImageLoading(false);
      return;
    }

    toast.error("Something went wrong, please try again later.");
    setImageLoading(false);
    return;
  };

  useEffect(() => {
    if (type === "update" && Object.keys(data).length !== 0) {
      if(!!data?.profileImagePath) setImageUrl(`${baseUrl}${data?.profileImagePath}`);
      const { name, email, cellNumber } = data?.captainInfo;
      const { length, draft, beam, hullType, yachtType } =
        data?.yachtSpecifications;

      // Create a copy to avoid mutating the prop
      const updatedData = { ...data };
      updatedData.name = name;
      updatedData.email = email;
      updatedData.cellNumber = cellNumber;
      updatedData.length = length;
      updatedData.draft = draft;
      updatedData.beam = beam;
      updatedData.hullType = hullType;
      updatedData.yachtType = yachtType;

      // Check if power required is a custom value
      if (updatedData?.powerRequired && !powers.includes(updatedData.powerRequired)) {
        setShowOtherPowerRequiredTextField(true);
        setUserSpecifiedPowerRequired(updatedData.powerRequired);
        updatedData.powerRequired = "Other , please specify";
      } else {
        setShowOtherPowerRequiredTextField(false);
        setUserSpecifiedPowerRequired("");
      }

      setUserId(updatedData?.id);
      setValues(updatedData);

      // Set insurance document name and URL
      if (data?.vesselInsurancePath) {
        setInsurance(data.vesselInsurancePath.split("/").pop());
        setInsuranceUrl(`${baseUrl}/${data.vesselInsurancePath}`);
      } else {
        setInsurance(null);
        setInsuranceUrl(null);
      }

      // Set registration document name and URL
      if (data?.vesselRegistrationPath) {
        setRegistration(data.vesselRegistrationPath.split("/").pop());
        setRegistrationUrl(`${baseUrl}/${data.vesselRegistrationPath}`);
      } else {
        setRegistration(null);
        setRegistrationUrl(null);
      }

      return;
    }
    return;
  }, [data]);

  const getValues = (target) => {
    const { name, value } = target;

    setValues({ ...values, [name]: value });

    if(name === "powerRequired") {
      if (value === "Other , please specify") {
        setShowOtherPowerRequiredTextField(true);
      } else {
        setShowOtherPowerRequiredTextField(false);
        setUserSpecifiedPowerRequired(""); // Clear custom value when switching away from "Other"
      }
    }
    return;
  };

  const preventMinus = (e) => {
    // console.log("key: ", e.key);
    if (e.code === "Minus") {
      e.preventDefault();
    }
  };

  const handleDocumentView = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const fileTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

  const handleInsuranceChange = (e) => {
    const file = e.target.files[0];
    if (file === undefined) {
      return;
    }

    if (!fileTypes.includes(file.type)) {
      toast.error(
        "Invalid file type. Only PNG, JPG, JPEG, and PDF files are allowed"
      );
      return;
    }

    setInsuranceFile(file);
    setInsurance(file?.name);
    return;
  };

  const handleRegistrationChange = (e) => {
    const file = e.target.files[0];
    if (file === undefined) {
      return;
    }

    if (!fileTypes.includes(file.type)) {
      toast.error(
        "Invalid file type. Only PNG, JPG, JPEG, and PDF files are allowed"
      );
      return;
    }

    setRegistrationFile(file);
    setRegistration(file?.name);
    return;
  };

  const handleFocusout = (e) => {
    if (e.target.name === "userName" && !validateEmail(e.target.value)) {
      setErrorField("userName");
      setInputFieldError(true);
      return;
    }
    setErrorField("");
    setInputFieldError(false);
    return;
  };

  const insuranceUpload = async (id) => {
    if (insuranceFile === null) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const res = await uploadInsurance(insuranceFile, id);

    if (res?.status === true) {
      toast.success("Vessel insurance uploaded successfully");
      setLoading(false);
      return;
    }

    toast.error("Something went wrong, please try again later.");
    setLoading(false);
    return;
  };

  const registrationUpload = async (id) => {
    if (registrationFile === null) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const res = await uploadRegistration(registrationFile, id);

    if (res?.status === true) {
      toast.success("Vessel registration uploaded successfully");
      setLoading(false);
      return;
    }

    toast.error("Something went wrong, please try again later.");
    setLoading(false);
    return;
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    setLoading(true);
    const {
      email,
      yachtName,
      imoHullNumber,
      powerRequired,
      hullType,
      beam,
      length,
      draft,
      cellNumber,
      userName,
      name,
      yachtType,
    } = values;

    const actualPowerRequired = showOtherPowerRequiredTextField ? userSpecifiedPowerRequired : powerRequired;
    const data = { userName, yachtName, imoHullNumber, powerRequired: actualPowerRequired };
    const yachtSpecifications = { length, beam, draft, yachtType, hullType };
    data.yachtSpecifications = yachtSpecifications;
    const captainInfo = { name, cellNumber, email };
    data.captainInfo = captainInfo;

    // console.log("f data: ", data);
    const res = await updateUserByUsername(data, token);

    if (res?.status === "Success") {
      await insuranceUpload(userId);
      await registrationUpload(userId);

      toast.success("Profile updated successfully");
      await refreshUserData();
      return;
    }

    if (res?.status === false) {
      setLoading(false);
      if (res?.message === "Invalid or expired access token.") {
        toast.error("token expired, please re-login and try");
        return;
      }

      toast.error("Something went wrong, please try again later");
      return;
    }

    setLoading(false);
    toast.error("Something went wrong, please try again later");
    return;
  };

  return (
    <div className="profile-layout">
      <Card>
        <CardContent>
          <Typography component="h4" variant="h4" align="center">
            Profile
          </Typography>
          <div className="profilepicContainer">
            <div className="profilepic">
              <img
                className="profile-pic circle"
                src={imageUrl || defaultProfileImage}
                alt="profile img"
              />
              <div className="p-image">
                <img
                  src="https://media.istockphoto.com/id/1440837734/vector/world-photography-day-photo-album-icon-photography-camera-black-icon-photo-book-take-a-pic.jpg?s=612x612&w=0&k=20&c=6VspBYS2BviPuvDIstXGoX0eXzuqEtX01JZq7gAx0z8="
                  alt="click to select profile"
                  onClick={() => imageRef.current.click()}
                />
                <input
                  ref={imageRef}
                  className="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
            </div>
            <Button
              disabled={imageFile === null}
              className="btn"
              variant="contained"
              color="primary"
              onClick={handleImageUpload}
              loading={imageLoading}
              loadingPosition="end"
            >
              Update Image
            </Button>
          </div>
          <form onSubmit={handleUpdate}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6">General Info</Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  type="email"
                  id="username"
                  name="userName"
                  label="Email"
                  size="small"
                  fullWidth
                  autoComplete="email"
                  value={values.userName || ""}
                  disabled={type === "update"}
                  onChange={(e) => getValues(e.target)}
                  onBlur={(e) => handleFocusout(e)}
                  error={errorField === "userName"}
                  helperText={
                    type === "update"
                      ? "You can't update your email"
                      : errorField === "userName" &&
                        "Please enter a valid email address"
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="yachtName"
                  name="yachtName"
                  label="Yacht Name"
                  size="small"
                  fullWidth
                  autoComplete="yachtName"
                  value={values.yachtName || ""}
                  onChange={(e) => getValues(e.target)}
                  onBlur={(e) => handleFocusout(e)}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  id="imoHullNumber"
                  name="imoHullNumber"
                  label="Registration number" // Referred in backend as IMO/HULL Number
                  size="small"
                  fullWidth
                  type="text"
                  autoComplete="imoHullNumber"
                  value={values.imoHullNumber || ""}
                  onChange={(e) => getValues(e.target)}
                  onBlur={(e) => handleFocusout(e)}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="h6">Yacht Specifications</Typography>
              </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl>
                        <FormLabel id="yacht-type-radio">Yacht type</FormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="yacht-type-radio"
                          name="yachtType"
                          value={values?.yachtType}
                          onChange={(e) => getValues(e.target)}
                        >
                          <FormControlLabel
                            checked={values?.yachtType === "Motor"}
                            value="Motor"
                            required
                            control={<Radio />}
                            label="Motor"
                          />
                          <FormControlLabel
                            checked={values?.yachtType === "Sailing"}
                            value="Sailing"
                            required
                            control={<Radio />}
                            label="Sailing"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl>
                        <FormLabel id="hull-type-radio">Hull type</FormLabel>
                        <RadioGroup
                          row
                          aria-labelledby="hull-type-radio"
                          name="hullType"
                          value={values?.hullType}
                          onChange={(e) => getValues(e.target)}
                        >
                          <FormControlLabel
                            checked={values?.hullType === "Mono hull"}
                            value="Mono hull"
                            control={<Radio />}
                            required
                            label="Mono hull"
                          />
                          <FormControlLabel
                            checked={values?.hullType === "Multi hull"}
                            value="Multi hull"
                            control={<Radio />}
                            required
                            label="Multi hull"
                          />
                          <FormControlLabel
                            checked={values?.hullType === "Trimaran"}
                            value="Trimaran"
                            control={<Radio />}
                            required
                            label="Trimaran"
                          />
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        id="length"
                        name="length"
                        label="Length (meters)"
                        size="small"
                        fullWidth
                        required
                        type="number"
                        // min="0"
                        autoComplete="length"
                        value={values.length || ""}
                        onChange={(e) => getValues(e.target)}
                        onKeyPress={preventMinus}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        id="beam"
                        name="beam"
                        label="Beam (meters)"
                        size="small"
                        fullWidth
                        required
                        type="number"
                        autoComplete="beam"
                        value={values.beam || ""}
                        onChange={(e) => getValues(e.target)}
                        onKeyPress={preventMinus}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        id="draft"
                        name="draft"
                        label="Draft (meters)"
                        size="small"
                        fullWidth
                        required
                        type="number"
                        autoComplete="draft"
                        value={values.draft || ""}
                        onChange={(e) => getValues(e.target)}
                        onKeyPress={preventMinus}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <FormControl size="small" fullWidth>
                        <InputLabel id="powerReq">Power requirement</InputLabel>
                        <Select
                          labelId="powerReq"
                          id="powerReqDrop"
                          fullWidth
                          required={type === "update" ? false : true}
                          name="powerRequired"
                          value={values?.powerRequired || ""}
                          onChange={(e) => getValues(e.target)}
                          label="Power requirement"
                        >
                          {powers.map((power) => (
                            <MenuItem
                              disabled={values?.powerRequired === power}
                              key={power}
                              value={power}
                            >
                              {power}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    {showOtherPowerRequiredTextField && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          required
                          id="userSpecifiedPowerRequired"
                          name="userSpecifiedPowerRequired"
                          label="Please specify your power requirement"
                          size="small"
                          fullWidth
                          value={userSpecifiedPowerRequired}
                          onChange={(e) => setUserSpecifiedPowerRequired(e.target.value)}
                          placeholder="e.g., 200 Amp 3-Phase"
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={12} className="fileContainer">
                      <Button
                        className="btn theme-btn"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        helperText="text"
                        onClick={() => uploadVesselInsuranceRef?.current?.click()}
                      >
                        Upload vessel insurance
                        <VisuallyHiddenInput
                          ref={uploadVesselInsuranceRef}
                          type="file"
                          onChange={handleInsuranceChange}
                          multiple
                        />
                      </Button>

                      {insurance && insuranceUrl ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => handleDocumentView(insuranceUrl)}
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: 'primary.main',
                            '&:hover': {
                              color: 'primary.dark',
                            }
                          }}
                        >
                          <InsertDriveFileIcon fontSize="small" />
                          {insurance}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {insurance || "no file chosen"}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12} md={12} className="fileContainer">
                      <Button
                        className="btn theme-btn"
                        role={undefined}
                        variant="contained"
                        tabIndex={-1}
                        onClick={() => uploadVesselRegistrationRef?.current?.click()}
                      >
                        Upload vessel Registration
                        <VisuallyHiddenInput
                          ref={uploadVesselRegistrationRef}
                          type="file"
                          onChange={handleRegistrationChange}
                          multiple
                        />
                      </Button>

                      {registration && registrationUrl ? (
                        <Link
                          component="button"
                          type="button"
                          variant="body2"
                          onClick={() => handleDocumentView(registrationUrl)}
                          sx={{
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            color: 'primary.main',
                            '&:hover': {
                              color: 'primary.dark',
                            }
                          }}
                        >
                          <InsertDriveFileIcon fontSize="small" />
                          {registration}
                        </Link>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {registration || "no file chosen"}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">Captain Info</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        required
                        id="name"
                        name="name"
                        label="Name"
                        size="small"
                        fullWidth
                        autoComplete="name"
                        value={values.name || ""}
                        onChange={(e) => getValues(e.target)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        type="number"
                        id="cellNumber"
                        name="cellNumber"
                        label="Cell Number"
                        size="small"
                        placeholder="+31 612345678 "
                        fullWidth
                        required
                        value={values.cellNumber || ""}
                        onChange={(e) => getValues(e.target)}
                        onKeyPress={preventMinus}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        type="email"
                        id="captainEmail"
                        name="email"
                        label="Email"
                        size="small"
                        autoComplete="email"
                        fullWidth
                        required
                        value={values.email || ""}
                        onChange={(e) => getValues(e.target)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="btn theme-btn"
                        loading={loading}
                        loadingPosition="end"
                      >
                        Update
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </div>
  );
};
